import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/feature/VideoCard';
import Pagination from '@/components/feature/Pagination';
import FilterPanel, { type FilterState } from './components/FilterPanel';
import { videos, categoryList } from '@/mocks/videos';
import type { VideoCategory } from '@/types/video';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 12;

const defaultFilter: FilterState = {
  genre: '全部',
  region: '全部',
  year: '全部',
  sort: 'hot',
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const meta = categoryList.find((c) => c.key === type);
  const categoryKey = (meta?.key ?? '') as VideoCategory;

  const categoryVideos = useMemo(
    () => videos.filter((v) => v.category === categoryKey),
    [categoryKey],
  );

  usePageMeta(
    meta ? `${meta.name}视频 - 涩涩视频热门视频聚合` : '分类 - 涩涩视频',
    meta
      ? `在涩涩视频观看最新${meta.name}视频，丰富多样海量资源随心筛选。`
      : '在涩涩视频浏览丰富多样的视频分类内容。',
  );

  const genres = useMemo(
    () => Array.from(new Set(categoryVideos.map((v) => v.genre))),
    [categoryVideos],
  );
  const regions = useMemo(
    () => Array.from(new Set(categoryVideos.map((v) => v.region))),
    [categoryVideos],
  );
  const years = useMemo(
    () => Array.from(new Set(categoryVideos.map((v) => String(v.year)))).sort((a, b) => Number(b) - Number(a)),
    [categoryVideos],
  );

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setFilter(defaultFilter);
    setPage(1);
  }, [type]);

  const filtered = useMemo(() => {
    const list = categoryVideos.filter((v) => {
      const okGenre = filter.genre === '全部' || v.genre === filter.genre;
      const okRegion = filter.region === '全部' || v.region === filter.region;
      const okYear = filter.year === '全部' || String(v.year) === filter.year;
      return okGenre && okRegion && okYear;
    });
    if (filter.sort === 'hot') return [...list].sort((a, b) => b.views - a.views);
    return [...list].sort((a, b) => (a.uploadDate < b.uploadDate ? 1 : -1));
  }, [categoryVideos, filter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const updateFilter = (patch: Partial<FilterState>) => {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(1);
  };
  const resetFilter = () => {
    setFilter(defaultFilter);
    setPage(1);
  };

  if (!meta) {
    return (
      <Layout>
        <div className="mx-auto flex min-h-[50vh] w-full flex-col items-center justify-center px-4 py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-folder-unknow-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">分类不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">暂时找不到这个分类，先去看看其他精彩内容吧。</p>
          <Link
            to="/"
            className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header band */}
      <section className="border-b border-background-200/70 bg-background-200/50">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 md:text-sm" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span>分类</span>
            <i className="ri-arrow-right-s-line" />
            <span className="font-medium text-foreground-900">{meta.name}</span>
          </nav>
          <div className="mt-4 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${meta.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                {meta.name}
              </h1>
              <p className="mt-1 text-xs text-foreground-500 md:text-sm">{meta.desc}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          {/* Desktop filter */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="lg:sticky lg:top-20">
              <FilterPanel
                genres={genres}
                regions={regions}
                years={years}
                value={filter}
                onChange={updateFilter}
                onReset={resetFilter}
              />
            </div>
          </aside>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 个{meta.name}视频
              </p>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(true)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 lg:hidden"
              >
                <i className="ri-equalizer-line" />
                筛选
              </button>
              <div className="hidden items-center gap-2 lg:flex">
                {(['hot', 'latest'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => updateFilter({ sort: s })}
                    className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      filter.sort === s
                        ? 'bg-secondary-900 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    {s === 'hot' ? '按热度' : '按最新'}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                  {pageItems.map((v, i) => (
                    <VideoCard key={v.id} video={v} rank={filter.sort === 'hot' && page === 1 ? i + 1 : undefined} />
                  ))}
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">没有匹配的视频</h2>
                <p className="mt-2 max-w-sm text-sm text-foreground-500">
                  当前筛选条件下暂时没有内容，换个条件或重置筛选试试看。
                </p>
                <button
                  type="button"
                  onClick={resetFilter}
                  className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line mr-1" />
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setMobileFilterOpen(false)}
            className="absolute inset-0 cursor-pointer bg-black/50"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] animate-fade-up overflow-y-auto rounded-t-lg bg-background-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-950">筛选与排序</span>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-600"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel
              genres={genres}
              regions={regions}
              years={years}
              value={filter}
              onChange={updateFilter}
              onReset={resetFilter}
            />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看 {filtered.length} 个结果
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}